import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { checkUserPaymentStatus, initiateLeaguePayment, supabase } from '../../lib/supabase';
import { useUserStore } from '../../lib/userStore';

export function LeaguePayment() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const { user_id } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [leagueInfo, setLeagueInfo] = useState<{
    name: string;
    fee: number;
    hasPaid: boolean;
  } | null>(null);

  useEffect(() => {
    if (!leagueId || !user_id) return;
    
    const loadLeagueInfo = async () => {
      try {
        // Récupérer les infos de la ligue
        const { data: league, error: leagueError } = await supabase
          .from('leagues')
          .select('name')
          .eq('league_id', leagueId)
          .single();

        if (leagueError) throw leagueError;

        // Récupérer les paramètres de paiement séparément
        const { data: settings, error: settingsError } = await supabase
          .from('league_settings')
          .select('league_fee, require_payment')
          .eq('league_id', leagueId)
          .single();

        if (settingsError) throw settingsError;

        if (!settings.require_payment) {
          toast.error('Cette ligue ne nécessite pas de paiement');
          navigate('/select-league');
          return;
        }

        // Vérifier le statut de paiement
        const { hasPaid } = await checkUserPaymentStatus(Number(leagueId), Number(user_id));
        
        if (hasPaid) {
          toast.success('Paiement déjà effectué pour cette ligue');
          navigate('/select-league');
          return;
        }

        setLeagueInfo({
          name: league.name,
          fee: settings.league_fee,
          hasPaid
        });
      } catch (error: any) {
        console.error('Erreur lors du chargement:', error);
        toast.error('Erreur lors du chargement des informations de la ligue');
        navigate('/select-league');
      }
    };
    
    loadLeagueInfo();
  }, [leagueId, user_id, navigate]);

  const handlePayment = async () => {
    if (!leagueId || !user_id) return;
    
    setLoading(true);
    try {
      const result = await initiateLeaguePayment(Number(leagueId), Number(user_id));
      
      if (!result.success) {
        throw new Error(result.error?.message || 'Erreur lors du paiement');
      }
      
      if (!result.paymentRequired) {
        toast.success('Aucun paiement requis pour cette ligue');
        navigate('/select-league');
      }
      
      // La redirection vers Stripe se fait automatiquement
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors du paiement');
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  if (!leagueInfo) {
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-lg border-0 mx-auto" style={{ maxWidth: '500px' }}>
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => navigate('/select-league')}
        >
          <ArrowLeft size={20} className="me-2" />
          Retour à la sélection
        </button>

        <div className="text-center mb-4">
          <h2 className="card-title mb-4">Paiement de la ligue</h2>
          
          <div className="mb-4">
            <h3 className="h5 mb-3 text-muted">{leagueInfo.name}</h3>
            <div className="display-4 text-primary mb-2">
              {formatPrice(leagueInfo.fee)} $ CAD
            </div>
            <p className="text-muted">
              Frais d'adhésion à la ligue
            </p>
          </div>

          <div className="d-grid gap-3">
            <button
              onClick={handlePayment}
              disabled={loading}
              className="btn btn-primary btn-lg"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Redirection vers le paiement...
                </>
              ) : (
                'Payer maintenant'
              )}
            </button>

            <button
              onClick={() => navigate('/select-league')}
              disabled={loading}
              className="btn btn-outline-secondary"
            >
              Annuler
            </button>
          </div>

          <p className="text-muted small mt-4">
            <i className="bi bi-shield-check me-1"></i>
            Paiement sécurisé via Stripe
          </p>
        </div>
      </div>
    </div>
  );
}
